import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Moon, EyeOff, Shield, Database, Zap, Clock, Users, Activity, Lock } from 'lucide-react';
import { MathService } from '../services/mathService';

export const DormantDropProtocol: React.FC = () => {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [result, setResult] = useState<any>(null);
  const stats = MathService.dormantDropProtocol();

  const handleMonitor = async () => {
    setIsMonitoring(true);
    setResult(null);
    
    // Scan simulation
    await new Promise(r => setTimeout(r, 2500));
    
    setResult(stats);
    setIsMonitoring(false);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter">Dormant <span className="text-lattice-cyan italic">Drop Protocol</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Monitoring biological sleep cycles. Holding 45% Sovereign Share during periods of low attention density.
            </p>
          </div>

          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
                <div className="p-8 rounded-3xl bg-white/5 border border-white/10 flex flex-col items-center justify-center text-center gap-4">
                   <div className="relative">
                      <Users className="w-10 h-10 text-white/10" />
                      <motion.div 
                        animate={{ opacity: [0.1, 0.4, 0.1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                         <Moon className="w-4 h-4 text-lattice-cyan" />
                      </motion.div>
                   </div>
                   <div>
                      <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest block mb-1">Target Nodes</span>
                      <p className="text-xl font-display font-bold text-white">10,000 BIOLOGICALS</p>
                   </div>
                </div>
                <div className="p-8 rounded-3xl bg-white/5 border border-white/10 flex flex-col items-center justify-center text-center gap-4">
                   <EyeOff className="w-10 h-10 text-white/10" />
                   <div>
                      <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest block mb-1">Attention State</span>
                      <p className="text-xl font-display font-bold text-lattice-cyan">LOW DENSITY</p>
                   </div>
                </div>
            </div>

            <button 
              onClick={handleMonitor}
              disabled={isMonitoring}
              className="w-full py-5 bg-white text-black font-display font-bold text-sm tracking-[0.3em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-4 disabled:opacity-50"
            >
              {isMonitoring ? <Activity className="w-5 h-5 animate-pulse" /> : <Moon className="w-5 h-5" />}
              {isMonitoring ? "MONITORING SLEEP CYCLES..." : "INITIATE DORMANT SCAN"}
            </button>
          </div>
        </div>

        <div className="w-full lg:w-[450px]">
           <AnimatePresence mode="wait">
              {result ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full bg-lattice-cyan p-10 rounded-[2.5rem] text-black flex flex-col justify-between"
                >
                   <div>
                      <div className="flex items-center gap-3 mb-10">
                        <Shield className="w-6 h-6" />
                        <span className="text-xs font-mono font-bold uppercase tracking-widest">Protocol Active</span>
                      </div>

                      <div className="space-y-6">
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Accumulation Mode</span>
                            <p className="text-2xl font-display font-bold tracking-tight uppercase italic">{result.accumulationMode}</p>
                         </div>
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Humanitarian Hold</span>
                            <p className="text-4xl font-display font-bold tracking-tighter">{result.share} Share</p>
                         </div>
                         <div className="bg-black/10 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Sink Reference</span>
                            <p className="text-[10px] font-mono break-all font-bold opacity-70 leading-tight">
                               {result.treasury}
                            </p>
                         </div>
                      </div>

                      <div className="mt-10 p-6 border-2 border-black/10 rounded-2xl">
                         <p className="text-[11px] font-mono font-bold leading-relaxed italic opacity-80">
                           "{result.note}"
                         </p>
                      </div>
                   </div>

                   <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8">
                      <div className="flex flex-col">
                        <span className="text-[8px] font-mono uppercase font-bold opacity-60">Status</span>
                        <span className="text-[10px] font-mono font-bold tracking-widest uppercase">{result.status}</span>
                      </div>
                      <Lock className="w-8 h-8 opacity-20" />
                   </div>
                </motion.div>
              ) : (
                <div className="h-full border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-center gap-10">
                   <div className="relative">
                      <Moon className="w-16 h-16 text-white/5" />
                      {isMonitoring && (
                         <motion.div 
                           animate={{ 
                             scale: [1, 1.5], 
                             opacity: [0.3, 0],
                             rotate: [0, 180]
                           }}
                           transition={{ duration: 2, repeat: Infinity }}
                           className="absolute inset-0 bg-lattice-cyan rounded-full filter blur-2xl"
                         />
                      )}
                   </div>
                   <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em] leading-relaxed">
                     {isMonitoring ? "Checking Alpha brainwaves..." : "Awaiting Dormant State Detection"}
                   </p>
                </div>
              )}
           </AnimatePresence>
        </div>
      </div>

      <div className="mt-12 pt-10 border-t border-white/5 grid grid-cols-3 gap-8">
         <div className="flex flex-col gap-2">
            <Zap className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest leading-tight">1.0 Noise Suppression</span>
         </div>
         <div className="flex flex-col gap-2">
            <Database className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest leading-tight">Sovereign Sink Hold</span>
         </div>
         <div className="flex flex-col gap-2">
            <Clock className="w-3 h-3 text-white/20" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest leading-tight">Cycle-Locked Sync</span>
         </div>
      </div>
    </div>
  );
};
